import React__default from 'react';
import { Text, Editor, Transforms } from 'slate';
import { Heading } from './components/Heading/Heading.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { isElement } from '../../../../core/utilities/nodes/elements/isElement.mjs';
import { isInline } from '../../../../core/queries/isInline.mjs';
import { isBlock } from '../../../../core/queries/isBlock.mjs';
import { getDerivedAttributes, getDerivedDataFromHTML } from '../../../../core/utilities/side-effects/combineSideEffects.mjs';
import { serialize } from '../../../../core/utilities/serialize/serialize.mjs';
import { getElementDeserializer } from '../../../../core/serialization/deserialize/utilities/getElementDeserializer.mjs';

const Type = 'heading';
const headingLevels = [1, 2, 3, 4, 5, 6];
const headingTagLevelMap = {
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
  H5: 5,
  H6: 6
};
const getEmptyHeading = level => {
  return serialize(getHeadingTag(level), '');
};
const HeadingPlugin = options => ({
  type: Type,
  onKeyDown: keyboardEvent => options != null && options.onHotkey ? options.onHotkey(keyboardEvent) : undefined,
  renderElement: getRenderElement({
    type: Type,
    rootProps: {},
    component(props) {
      const attributes = getDerivedAttributes({
        sideEffects: options == null ? void 0 : options.sideEffects,
        element: props.element
      });
      return /*#__PURE__*/React__default.createElement(Heading, Object.assign({}, props, {
        style: attributes.style,
        level: props.element.level
      }));
    }
  }),
  extendEditor(editor) {
    const {
      normalizeNode,
      insertBreak
    } = editor;
    editor.normalizeNode = entry => {
      const [node, path] = entry;

      // Ensure only inlines or text nodes are nested within headings
      if (!Text.isText(node) && isElement(node) && !isInline(editor, node)) {
        const headingAncestor = Editor.above(editor, {
          at: path,
          match: isHeading
        });
        if (headingAncestor) {
          Transforms.liftNodes(editor, {
            at: path
          });
          return;
        }
      }

      // Fall back to the original `normalizeNode` to enforce other constraints.
      normalizeNode(entry);
    };
    editor.insertBreak = () => {
      var _Editor$above;
      const at = editor.selection;
      if (!at) {
        return;
      }
      const [, headingPath] = (_Editor$above = Editor.above(editor, {
        at,
        match: isHeading
      })) != null ? _Editor$above : [];
      if (headingPath) {
        const [headingParent] = Editor.parent(editor, headingPath);

        // If the heading is wrapped in a block level element
        if (isElement(headingParent) && isBlock(editor, headingParent)) {
          Editor.withoutNormalizing(editor, () => {
            // Split the block level element instead of the Heading
            Transforms.splitNodes(editor, {
              match: node => isElement(node) && node.type === headingParent.type,
              always: true
            });
            // Unwrap the heading element from the new line
            Transforms.unwrapNodes(editor, {
              match: isHeading
            });
          });
          return;
        }
      }

      // Fall back to the original `insertBreak`
      insertBreak();
    };
    return editor;
  },
  serialize: {
    after(html) {
      let newHTML = html;
      headingLevels.forEach(level => {
        if (html === getEmptyHeading(level)) {
          newHTML = '';
        }
      });
      return newHTML;
    },
    element(props) {
      const {
        children,
        element: {
          level
        }
      } = props;
      const attributes = getDerivedAttributes({
        sideEffects: options == null ? void 0 : options.sideEffects,
        element: props.element
      });
      const headingElement = getHeadingTag(level);
      return serialize(headingElement, children, attributes);
    }
  },
  deserialize: {
    element: getElementDeserializer({
      type: Type,
      rules: [{
        nodeNames: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']
      }],
      node: el => {
        const headingNodeData = {
          type: Type,
          level: parseHeadingTag(el.tagName)
        };
        return getDerivedDataFromHTML({
          sideEffects: options == null ? void 0 : options.sideEffects,
          data: headingNodeData,
          htmlElement: el
        });
      }
    })
  }
});
function isHeading(node) {
  return isElement(node) && node.type === Type;
}
function parseHeadingTag(tagName) {
  return headingTagLevelMap[tagName] || 1;
}
function getHeadingTag(level) {
  return `h${level}` || 'h1';
}

export { HeadingPlugin, Type, getHeadingTag, headingLevels, isHeading, parseHeadingTag };
